/**
 * Locale resolution for the ANONYMOUS patient pages (reorder-v2.1 hardening item 3).
 *
 * The web app's i18n init pins `lng: 'en'` and never auto-detects from the
 * browser (a deliberate Rafael preference for the authenticated app) — so the
 * patient pages, which have no LanguageSwitcher, always rendered English even for
 * Arabic-first patients. This module closes that gap WITHOUT touching the global
 * i18n config: a patient-page mount effect resolves the desired locale at runtime
 * and calls `i18n.changeLanguage`, which the existing global DirectionEffect
 * turns into `<html dir=rtl|ltr>` automatically.
 *
 * Persistence uses a DEDICATED key (`insolech-patient-locale`) written only by the
 * patient switcher. We deliberately do NOT read i18next's own `insolech-locale`
 * cache: i18next writes `insolech-locale='en'` on init (caches:['localStorage']),
 * so that key can't tell an explicit patient choice apart from the boot default —
 * reading it would defeat auto-detection entirely. The separate key also keeps the
 * patient choice fully isolated from the authenticated app (which boots at
 * lng:'en' and never reads either key back), so there is no cross-context leak.
 */
import { LOCALES, DEFAULT_LOCALE, isLocale, type Locale } from '@insolech/i18n';

/** Dedicated key for an EXPLICIT patient language choice (switcher only). */
export const PATIENT_LOCALE_STORAGE_KEY = 'insolech-patient-locale';

/**
 * Map the browser's preferred languages to a supported locale (en/es/ar), taking
 * the first match in the user's order, falling back to English. Base-language
 * only ("ar-AE" -> "ar"), case-insensitive.
 */
export function detectBrowserLocale(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  const prefs =
    Array.isArray(navigator.languages) && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language];
  for (const raw of prefs) {
    const base = (raw ?? '').toLowerCase().split('-')[0];
    if ((LOCALES as readonly string[]).includes(base)) return base as Locale;
  }
  return DEFAULT_LOCALE;
}

/**
 * The locale a patient page should display: a previously stored EXPLICIT choice
 * if valid, otherwise the browser-detected locale (en fallback).
 */
export function resolvePatientLocale(): Locale {
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(PATIENT_LOCALE_STORAGE_KEY);
      if (stored && isLocale(stored)) return stored;
    } catch {
      // localStorage can throw in private-mode / blocked-cookie contexts; fall through.
    }
  }
  return detectBrowserLocale();
}

/** Persist an explicit patient language choice (from the switcher). */
export function persistPatientLocale(locale: Locale): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PATIENT_LOCALE_STORAGE_KEY, locale);
  } catch {
    // ignore private-mode / quota failures — the in-memory language still applies.
  }
}
